import type { AuditResult } from '../rules/engine'
import type { Finding, Severity } from '../rules/types'

const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
}

const ORDER: Severity[] = ['critical', 'high', 'medium', 'low', 'info']

/**
 * Renders an audit into `container`.
 *
 * Built with DOM nodes and `textContent` rather than innerHTML: findings quote values
 * taken straight from a config the user pasted, and a device named `<img onerror=...>`
 * must render as text, not execute. Since the whole tool is client-side there is no
 * server-side sanitiser to fall back on.
 */
export function renderAudit(container: HTMLElement, result: AuditResult): void {
  container.replaceChildren()

  if (result.warnings.length > 0) {
    container.append(
      note(
        result.warnings.length === 1
          ? `Note: ${result.warnings[0]!.message} (line ${result.warnings[0]!.line}). Findings below cover the part that parsed.`
          : `Note: the configuration had ${result.warnings.length} structural problems, most likely from a truncated copy. Findings below cover the part that parsed.`,
      ),
    )
  }

  if (result.findings.length === 0) {
    container.append(cleanResult(result.rulesRun))
    return
  }

  container.append(summary(result))
  for (const f of result.findings) container.append(findingCard(f))
}

function note(text: string): HTMLElement {
  const el = document.createElement('p')
  el.className = 'notice'
  el.textContent = text
  return el
}

function summary(result: AuditResult): HTMLElement {
  const el = document.createElement('div')
  el.className = 'summary'

  const headline = document.createElement('span')
  headline.className = 'headline'
  const n = result.findings.length
  headline.textContent = `${n} finding${n === 1 ? '' : 's'} from ${result.rulesRun} checks`
  el.append(headline)

  const tally = document.createElement('span')
  tally.className = 'tally'
  const parts = ORDER.filter((s) => result.counts[s] > 0).map((s) => `${result.counts[s]} ${SEVERITY_LABEL[s].toLowerCase()}`)
  tally.textContent = parts.join(' · ')
  el.append(tally)

  return el
}

function cleanResult(rulesRun: number): HTMLElement {
  const el = document.createElement('div')
  el.className = 'clean'

  const h = document.createElement('h2')
  h.textContent = `No findings — all ${rulesRun} checks passed`
  el.append(h)

  const p = document.createElement('p')
  p.textContent =
    'Nothing in this configuration matched a check. That is a real result, not a fallback: ' +
    'the checks that did not fire are listed in the footer.'
  el.append(p)

  return el
}

function findingCard(f: Finding): HTMLElement {
  const card = document.createElement('article')
  card.className = 'finding'
  card.dataset['severity'] = f.severity

  const head = document.createElement('div')
  head.className = 'finding-head'

  const sev = document.createElement('span')
  sev.className = 'sev'
  sev.textContent = SEVERITY_LABEL[f.severity]
  head.append(sev)

  const title = document.createElement('h3')
  title.textContent = f.title
  head.append(title)
  card.append(head)

  const meta = document.createElement('p')
  meta.className = 'meta'
  for (const text of metaParts(f)) {
    const span = document.createElement('span')
    span.textContent = text
    meta.append(span)
  }
  if (meta.childElementCount > 0) card.append(meta)

  const detail = document.createElement('p')
  detail.className = 'detail'
  detail.textContent = f.detail
  card.append(detail)

  const fix = document.createElement('details')
  const summaryEl = document.createElement('summary')
  summaryEl.textContent = 'How to fix'
  fix.append(summaryEl)
  const pre = document.createElement('pre')
  pre.textContent = f.remediation
  fix.append(pre)
  card.append(fix)

  return card
}

function metaParts(f: Finding): string[] {
  const parts: string[] = []
  if (f.location !== undefined) parts.push(f.location)
  if (f.line !== undefined) parts.push(`line ${f.line}`)
  if (f.cis !== undefined) parts.push(`CIS ${f.cis}`)
  if (f.pci !== undefined) parts.push(`PCI DSS ${f.pci}`)
  parts.push(f.ruleId)
  return parts
}
